import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Grid,
  useToast,
} from "@chakra-ui/react";
import { logError } from "@verivest/shared-components/src/error-handling/logError";
import React from "react";
import useSWR from "swr";
import { updateHoldingLabel } from "../../api/creators/holding";
import useApi from "../../hooks/useApi";
import useDynamicConfig, { MANAGER } from "../../hooks/useDynamicConfig";
import usePlacementsApi from "../../hooks/usePlacementsApi";
import {
  dateToMonYYYYUTC,
  formatMoneyWithSymbol,
  formatNumber,
  toastError,
  toastSuccess,
} from "../../utils/utils";
import { LabelAndValue } from "../general";
import VeriInput from "../general/Input";
import PopoverEdit from "../general/PopoverEdit";
import { ColoredTag } from "@verivest/shared-components";

const HoldingDetails = ({
  accountId,
  offeringId,
  isDrawer,
  showHoldings,
  showEditor,
  showUnitizedInfo,
}) => {
  const placementsApi = usePlacementsApi();
  const api = useApi();
  const dynamicConfig = useDynamicConfig();
  const managers = dynamicConfig.get(MANAGER);
  const toast = useToast();

  const [expanded, setExpanded] = React.useState([]);

  const { data: dataHoldings, mutate: mutateHolding } = useSWR(
    `/holdings?${
      managers?.id ? "manager_id=" + managers.id + "&" : ""
    }account_id=${accountId}&offering_id=${offeringId}`,
    placementsApi.apiRequest,

    {
      focusThrottleInterval: 0,
    },
  );

  if (!dataHoldings) return <></>;

  const { data: holdings } = dataHoldings || [];

  const _holdings = holdings.filter(
    (holding) => holding.offering.id === offeringId,
  );

  const handleUpdateHoldingLabel = async ({ label, id }) => {
    try {
      await updateHoldingLabel(api, id, label);
      await mutateHolding();
      toastSuccess(toast, "Holding label updated");
    } catch (e) {
      toastError(toast, "Something went wrong");
      logError(e);
    }
  };

  return (
    <Box py={8}>
      <Accordion allowMultiple onChange={(e) => setExpanded(e)}>
        {_holdings.length === 0 && (
          <Flex align="center" justify="center" p={8}>
            <Box>No holdings found</Box>
          </Flex>
        )}
        {_holdings.map((holding, index) => {
          const isUnitized = !!holding?.security?.isUnitized;
          return (
            <AccordionItem
              key={index}
              borderTop={"none"}
              borderBottom={"none"}
              px={0}
              {...(expanded.includes(index) && {
                backgroundColor: "bg.white",
                boxShadow: isDrawer ? 0 : 2,
                borderRadius: isDrawer ? 0 : 4,
                mt: index > 0 ? 6 : 0,
                mx: -6,
                px: 6,
              })}
              transition={"all 0.2s ease-in-out"}
            >
              <AccordionButton
                as={"div"}
                p={0}
                _hover={{ backgroundColor: "transparent", cursor: "pointer" }}
              >
                <Flex
                  flexGrow={1}
                  borderBottom={"1px solid"}
                  borderBottomColor={"border.grey"}
                  borderTop={"1px solid"}
                  borderTopColor={index > 0 ? "transparent" : "border.grey"}
                  {...(!isDrawer &&
                    expanded.includes(index) && {
                      borderTopColor: "transparent",
                    })}
                  py={6}
                >
                  <Box flexGrow={1}>
                    <Flex
                      alignItems={"center"}
                      justifyContent={"flex-start"}
                      gap={2}
                      textStyle={"bodyRegularBold"}
                    >
                      <Box as={"span"}>{holding?.security?.name}</Box>
                      {showUnitizedInfo && (
                        <ColoredTag type={isUnitized ? "success" : "error"}>
                          {isUnitized ? "Unitized" : "Non-unitized"}
                        </ColoredTag>
                      )}
                    </Flex>
                    {showHoldings && (
                      <Flex
                        textAlign={"left"}
                        textStyle={"bodySmall"}
                        color={"text.greyDark"}
                        alignItems={"center"}
                      >
                        <Box>{holding?.label || `Holding #${holding?.id}`}</Box>
                        {showEditor && (
                          <PopoverEdit
                            onSubmit={handleUpdateHoldingLabel}
                            defaultValues={{
                              label:
                                holding?.label || `Holding #${holding?.id}`,
                              id: holding?.id,
                            }}
                            getInputs={(form) => (
                              <>
                                <VeriInput
                                  {...form.register("label", {
                                    required: "You must enter a valid label",
                                  })}
                                  error={form.formState?.errors?.label?.message}
                                  w={"100%"}
                                />
                              </>
                            )}
                          />
                        )}
                      </Flex>
                    )}
                  </Box>
                  <Box mx={5} textStyle={"bodyRegular"} fontWeight={400}>
                    {!holding.offering?.hideMarketValue && (
                      <LabelAndValue
                        labelProps={{
                          textTransform: "none",
                          textAlign: "right",
                        }}
                        label="Total Value"
                        mb={0}
                        textAlign={"right"}
                      >
                        {formatMoneyWithSymbol(holding.marketValue)}
                      </LabelAndValue>
                    )}
                  </Box>
                  <Box as={"button"}>
                    <AccordionIcon color={"icons.grey"} />
                  </Box>
                </Flex>
              </AccordionButton>
              <AccordionPanel px={0} py={6}>
                <Grid
                  templateColumns={"repeat(" + (isDrawer ? 2 : 3) + ", 1fr)"}
                  gap={6}
                >
                  {!holding.offering?.hideMarketValue && (
                    <LabelAndValue label="Total Value">
                      {formatMoneyWithSymbol(holding.marketValue)}
                      {holding?.asOfDate && (
                        <Box color={"text.grey"} textStyle={"bodySmall"}>
                          as of {dateToMonYYYYUTC(holding.asOfDate, true)}
                        </Box>
                      )}
                    </LabelAndValue>
                  )}

                  {holding?.security?.securityType === "Equity" && (
                    <>
                      <LabelAndValue
                        label="Net Capital Contributions"
                        value={formatMoneyWithSymbol(
                          holding.netCapitalContributions,
                        )}
                      />
                      {isUnitized && (
                        <>
                          <LabelAndValue
                            label={"Units Held"}
                            value={formatNumber(holding.units, 8)}
                          />
                          <LabelAndValue
                            label={"Unit Price"}
                            value={
                              formatMoneyWithSymbol(holding.unitPrice, 8) +
                              "/unit"
                            }
                          />
                          <LabelAndValue
                            label={"Unit Price Date"}
                            value={dateToMonYYYYUTC(
                              holding.unitPriceDate,
                              true,
                            )}
                          />
                        </>
                      )}
                    </>
                  )}

                  {holding?.security?.securityType === "Debt" && (
                    <>
                      <LabelAndValue
                        label="Net Capital Contributions"
                        value={formatMoneyWithSymbol(
                          holding.netCapitalContributions,
                        )}
                      />
                    </>
                  )}

                  {holding?.security?.securityType === "Equity" && (
                    <>
                      <LabelAndValue
                        label={"Preferred Return Rate"}
                        value={
                          Number(holding.rate) === 0
                            ? "none"
                            : formatNumber(holding.rate * 100, 2) + "%"
                        }
                      />

                      {Number(holding.rate) !== 0 && !!holding.paidToDate && (
                        <LabelAndValue
                          label={"Pref Paid to Date"}
                          value={dateToMonYYYYUTC(holding.paidToDate, true)}
                        />
                      )}

                      <LabelAndValue
                        label={"Inception date"}
                        value={
                          holding.inceptionDate
                            ? dateToMonYYYYUTC(holding.inceptionDate, true)
                            : "None"
                        }
                      />
                    </>
                  )}

                  {holding?.security?.securityType === "Debt" && (
                    <>
                      <LabelAndValue
                        label={"Interest Rate"}
                        value={
                          holding.rate
                            ? formatNumber(holding.rate * 100, 2) + "%"
                            : "None"
                        }
                      />

                      {Number(holding.rate) !== 0 && (
                        <LabelAndValue
                          label={"Interest Paid to Date"}
                          value={
                            holding.paidToDate
                              ? dateToMonYYYYUTC(holding.paidToDate, true)
                              : "None"
                          }
                        />
                      )}

                      <LabelAndValue
                        label={"Inception date"}
                        value={
                          holding.inceptionDate
                            ? dateToMonYYYYUTC(holding.inceptionDate, true)
                            : "None"
                        }
                      />

                      <LabelAndValue
                        label={"Maturity Date"}
                        value={
                          holding.maturityDate
                            ? dateToMonYYYYUTC(holding.maturityDate, true)
                            : "None"
                        }
                      />
                    </>
                  )}
                </Grid>
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </Box>
  );
};

export default HoldingDetails;
